import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconCloud, IconDownload } from "@tabler/icons-react";
import AttendanceInteractionForm from "components/attendances/AttendanceInteractionForm";
import AttendanceStatusChip from "components/attendances/AttendanceStatusChip";
import {
  ATTENDANCE_STATUSES,
  formatAttendanceDateTime,
  formatCpf,
  formatFileSize,
  isAttendanceOpen,
} from "utils/attendance";

const MetaItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const driveLabel = (attachment) => {
  if (attachment.drive_status === "SENT") return "Enviado ao Drive";
  if (attachment.drive_status === "FAILED") return "Falha no Drive";
  return "Pendente no Drive";
};

const AttendanceDetailModal = ({
  open,
  attendanceId,
  onClose,
  canAddInteraction,
  onAttendanceUpdated,
}) => {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statuses, setStatuses] = useState(ATTENDANCE_STATUSES);
  const [statusSaving, setStatusSaving] = useState(false);

  const loadAttendance = async (id, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const response = await fetch(`/backend/attendances/${id}/`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.detail || "Não foi possível carregar o atendimento.");
      }
      setAttendance(payload);
      onAttendanceUpdated?.(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (open && attendanceId) {
      loadAttendance(attendanceId);
    } else {
      setAttendance(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attendanceId]);

  useEffect(() => {
    if (!open) return;
    fetch("/backend/attendances/statuses/")
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => setStatuses(Array.isArray(data) ? data : []))
      .catch(() => setStatuses([]));
  }, [open]);

  const handleDownload = async (attachment) => {
    try {
      const response = await fetch(attachment.download_url || `/backend/attendance-attachments/${attachment.id}/download/`);
      if (!response.ok) throw new Error("Não foi possível baixar o arquivo.");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.file_name || "arquivo";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleInteractionCreated = () => {
    loadAttendance(attendanceId, { silent: true });
  };

  const handleStatusChange = async (nextStatus) => {
    if (!attendance?.id || !nextStatus || nextStatus === attendance.status) return;
    setStatusSaving(true);
    setError("");
    try {
      const response = await fetch(`/backend/attendances/${attendance.id}/status/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = payload.detail || payload.status || "Não foi possível alterar o status.";
        throw new Error(Array.isArray(message) ? message[0] : message);
      }
      await loadAttendance(attendance.id, { silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setStatusSaving(false);
    }
  };

  const isOpenAttendance = isAttendanceOpen(attendance?.status);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, pr: 2 }}>
        {attendance?.identifier || "Detalhes do atendimento"}
        {attendance?.status ? (
          <AttendanceStatusChip status={attendance.status} label={attendance.status_display} />
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {attendance && !loading && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 1.5,
                mb: 3,
              }}
            >
              <MetaItem label="Identificador" value={attendance.identifier} />
              <MetaItem label="Cliente" value={attendance.customer_name} />
              <MetaItem label="CPF" value={formatCpf(attendance.customer_cpf)} />
              <MetaItem label="Processo" value={attendance.process_number} />
              <MetaItem label="Consultor" value={attendance.sales_consultant_name} />
              <MetaItem label="Motivo" value={attendance.subject_display} />
              <MetaItem label="Criado por" value={attendance.created_by_name} />
              <MetaItem label="Data de abertura" value={formatAttendanceDateTime(attendance.created_at)} />
              <MetaItem label="Interações" value={String(attendance.interactions_count || 0)} />
              <MetaItem label="Documentos" value={String(attendance.attachments_count || 0)} />
              {canAddInteraction ? (
                <Box sx={{ minWidth: 0 }}>
                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={attendance.status || ""}
                    onChange={(event) => handleStatusChange(event.target.value)}
                    disabled={statusSaving}
                    fullWidth
                  >
                    {statuses.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              ) : (
                <MetaItem label="Status" value={attendance.status_display} />
              )}
            </Box>

            {!isOpenAttendance && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este atendimento está {String(attendance.status_display || attendance.status).toLowerCase()}.
                {canAddInteraction ? " Reabra para registrar novas interações." : ""}
              </Alert>
            )}

            <Typography variant="h5" sx={{ mb: 1.5 }}>
              Histórico
            </Typography>
            {(attendance.interactions || []).map((interaction) => (
              <Box
                key={interaction.id}
                sx={{
                  mb: 2,
                  p: 2,
                  borderLeft: "3px solid",
                  borderColor: "secondary.main",
                  bgcolor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {formatAttendanceDateTime(interaction.created_at)}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {interaction.user_name || "—"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
                  {interaction.description}
                </Typography>
                {(interaction.attachments || []).length > 0 && (
                  <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                    {interaction.attachments.map((attachment) => (
                      <Box
                        key={attachment.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          p: 1,
                          bgcolor: "background.paper",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 140 }}>
                          {attachment.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(attachment.size)} · {formatAttendanceDateTime(attachment.created_at)} · {attachment.uploaded_by_name}
                        </Typography>
                        <Chip
                          size="small"
                          icon={<IconCloud stroke={1.5} size="0.9rem" />}
                          label={driveLabel(attachment)}
                          color={attachment.drive_status === "SENT" ? "success" : attachment.drive_status === "FAILED" ? "warning" : "default"}
                          variant="outlined"
                        />
                        <Tooltip title="Baixar">
                          <IconButton size="small" onClick={() => handleDownload(attachment)}>
                            <IconDownload stroke={1.5} size="1.1rem" />
                          </IconButton>
                        </Tooltip>
                        {attachment.drive_url && attachment.drive_status === "SENT" && (
                          <Button size="small" href={attachment.drive_url} target="_blank" rel="noreferrer">
                            Abrir no Drive
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}

            {canAddInteraction && isOpenAttendance && (
              <>
                <Divider sx={{ my: 2 }} />
                <AttendanceInteractionForm
                  attendanceId={attendance.id}
                  onCreated={handleInteractionCreated}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceDetailModal;
